import PropTypes from "prop-types";
import { useId } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { IconCalendar } from "@tabler/icons";
import ActionButton from "./ActionButton";
import { todayIso, typedFromIso } from "./days";

// A day staff type as DD/MM/YYYY, with a "Today" button beside it. The value
// is what was typed (never cleared or reformatted under the person); the
// caller turns it into YYYY-MM-DD with isoFromTyped and checks it with
// typedDayError. showToday={false} for a day that is never today (a visa's
// last valid day).
export default function DayField({ label, optional, hint, error, value, onChange, disabled, todayLabel = "Today", showToday = true, inputRef }) {
  const id = useId();
  return (
    <Box>
      <Box component="label" htmlFor={id} sx={{ display: "block", fontWeight: 600, mb: 0.75 }}>
        {label}
        {optional && (
          <Typography component="span" color="text.secondary" fontWeight={400}>
            {" "}
            (optional)
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          id={id}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="DD/MM/YYYY"
          disabled={disabled}
          error={!!error}
          inputRef={inputRef}
          inputProps={{ inputMode: "numeric", autoComplete: "off", maxLength: 10, "aria-invalid": error ? "true" : "false", "aria-describedby": `${id}-help` }}
          sx={{ width: 180, "& .MuiInputBase-root": { minHeight: 48, borderRadius: "14px" }, "& input": { fontFamily: "monospace" } }}
        />
        {showToday && (
          <ActionButton
            variant="outlined"
            size="medium"
            disabled={disabled}
            startIcon={<IconCalendar size={18} />}
            onClick={() => onChange(typedFromIso(todayIso()))}
            aria-label={`${todayLabel}: set ${typeof label === "string" ? label.toLowerCase() : "the date"} to today`}
          >
            {todayLabel}
          </ActionButton>
        )}
      </Stack>
      <Typography
        id={`${id}-help`}
        variant="body2"
        sx={{
          mt: 0.5,
          color: (t) => (error ? (t.palette.mode === "dark" ? "#FDA29B" : "#B42318") : t.palette.text.secondary),
          fontWeight: error ? 600 : 400,
        }}
      >
        {error || hint}
      </Typography>
    </Box>
  );
}

DayField.propTypes = {
  label: PropTypes.node.isRequired,
  optional: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  todayLabel: PropTypes.string,
  showToday: PropTypes.bool,
  inputRef: PropTypes.any,
};
